import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Table, Button, ButtonGroup, Row, Col, Badge, Alert, Spinner } from "react-bootstrap";
import { Pencil, Trash2, Lock, Unlock, RefreshCw, KeyRound, LayoutGrid, List, Phone, CalendarDays, Mail, Link2, Wallet, Copy, Check, Eye, EyeOff } from "lucide-react";
import { clearFopLabelsCache } from "../hooks/useFopLabels";
import WorkshopWorkersCrud from "../components/WorkshopWorkersCrud";
import UserAvatar from "../components/UserAvatar";
import UserFormModal from "../components/UserFormModal";
import KeycrmRolesModal from "../components/KeycrmRolesModal";
import { roleLabel } from "../helpers/roles";
import { formatPhone, phoneE164 } from "../helpers/phone";
import "./UsersPage.css";

function formatDateTime(v) {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const VIEW_STORAGE_KEY = "usersPage.view";

function readStoredView() {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === "table" ? "table" : "cards";
    } catch {
        return "cards";
    }
}

/** Телефон: клік — набрати (tel:), іконка поруч — скопіювати. */
function PhoneLink({ raw, className = "" }) {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const display = formatPhone(raw);
    if (!display) return null;
    const e164 = phoneE164(raw);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(e164 || display);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1500);
        } catch {
            /* буфер недоступний — нічого не робимо */
        }
    };

    return (
        <span className={`users-phone ${className}`}>
            <a href={`tel:${e164}`} className="users-phone__link" title="Подзвонити">
                {display}
            </a>
            <button
                type="button"
                className={`users-phone__copy${copied ? " users-phone__copy--done" : ""}`}
                title={copied ? "Скопійовано" : "Скопіювати"}
                aria-label="Скопіювати номер"
                onClick={copy}
            >
                {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
            </button>
        </span>
    );
}

/** Рядок реквізиту: значення + іконка копіювання справа (з'являється при наведенні). */
function CopyValueRow({ label, value, display, mono = false }) {
    if (!value) return null;
    return (
        <div className="users-req__row">
            <span className={`users-req__value${mono ? " users-req__value--mono" : ""}`} title={label}>
                {display || value}
            </span>
            <CopyButton text={value} label={`Скопіювати ${label}`} />
        </div>
    );
}

const formatIbanDisplay = (iban) => String(iban || "").replace(/(.{4})(?=.)/g, "$1 ");

/** Кнопка «скопіювати» з галочкою після копіювання. */
function CopyButton({ text, label, className = "" }) {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1500);
        } catch {
            /* буфер недоступний */
        }
    };

    return (
        <button
            type="button"
            className={`users-phone__copy${copied ? " users-phone__copy--done" : ""} ${className}`}
            title={copied ? "Скопійовано" : label}
            aria-label={label}
            onClick={copy}
        >
            {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
        </button>
    );
}

function RequisitesBlock({ title, data }) {
    if (!data?.fullName && !data?.ipn && !data?.iban) return null;
    // «Скопіювати все»: лише значення, по рядку на реквізит, IBAN без пробілів
    const allText = [data.fullName, data.ipn, data.iban].filter(Boolean).join("\n");
    return (
        <div className="users-req">
            <div className="users-req__head">
                <span className="users-req__title">{title}</span>
                <CopyButton text={allText} label={`Скопіювати всі ${title.toLowerCase()}`} className="users-req__copy-all" />
            </div>
            <CopyValueRow label="ПІБ" value={data.fullName} />
            <CopyValueRow label="ІПН" value={data.ipn} mono />
            {/* показуємо з пробілами, копіюємо без — так IBAN приймають банки */}
            <CopyValueRow label="IBAN" value={data.iban} display={formatIbanDisplay(data.iban)} mono />
        </div>
    );
}

/**
 * Біла картка Mono: за замовчуванням «•••• 1234»; око — розшифрувати (запит лише для адміна),
 * тоді номер групами по 4 з копіюванням без пробілів, власник і термін дії.
 */
function MonoCardBlock({ user }) {
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const last4 = user.integrations?.monoCard?.last4;
    if (!last4) return null;

    const toggle = async () => {
        if (card) {
            setCard(null);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`/api/users/${user._id}/mono-card`, { withCredentials: true });
            setCard(res.data);
        } catch (e) {
            alert(e.response?.data?.error || "Не вдалося показати дані картки");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="users-req">
            <div className="users-req__head">
                <span className="users-req__title">Біла картка</span>
                <button
                    type="button"
                    className="users-phone__copy users-req__reveal"
                    title={card ? "Сховати" : "Показати"}
                    aria-label={card ? "Сховати дані картки" : "Показати дані картки"}
                    disabled={loading}
                    onClick={toggle}
                >
                    {card ? <EyeOff size={13} aria-hidden /> : <Eye size={13} aria-hidden />}
                </button>
            </div>
            {card ? (
                <>
                    <CopyValueRow
                        label="Номер картки"
                        value={String(card.number || "").replace(/\s+/g, "")}
                        display={formatIbanDisplay(String(card.number || "").replace(/\s+/g, ""))}
                        mono
                    />
                    <CopyValueRow label="Власник" value={card.holder} />
                    <CopyValueRow label="Діє до" value={card.expiry} mono />
                </>
            ) : (
                <div className="users-req__row">
                    <span className="users-req__value users-req__value--mono">•••• •••• •••• {last4}</span>
                </div>
            )}
        </div>
    );
}

/** Друга колонка картки: реквізити ЗП і, для ФОП, реквізити рахунку ФОП (лише для адміна — іншим сервер їх не віддає). */
function RequisitesPanel({ user }) {
    const isFop = user.profile?.fopTitlePrefix === "ФОП ";
    const fopData = isFop ? user.integrations?.monoFop : null;
    const filled = (d) => !!(d?.fullName || d?.ipn || d?.iban);
    const cardKey = fopData?.cardKey;
    const hasMonoCard = isFop && !!user.integrations?.monoCard?.last4;
    if (!filled(user.payout) && !filled(fopData) && !cardKey && !hasMonoCard) return null;
    return (
        <div className="users-card__col users-card__col--req">
            <RequisitesBlock title="Реквізити для ЗП" data={user.payout} />
            {fopData ? <RequisitesBlock title="Реквізити ФОП" data={fopData} /> : null}
            {cardKey ? (
                <div className="users-req">
                    <div className="users-req__head">
                        <span className="users-req__title">Ключ карта</span>
                    </div>
                    {/* показуємо групами по 4, копіюємо без пробілів */}
                    <CopyValueRow label="Ключ карта" value={cardKey.replace(/\s+/g, "")} display={formatIbanDisplay(cardKey.replace(/\s+/g, ""))} mono />
                </div>
            ) : null}
            {hasMonoCard ? <MonoCardBlock user={user} /> : null}
        </div>
    );
}

function CrmStatusBadge({ status, className = "ms-2" }) {
    if (!status) return null;
    return (
        <Badge bg={status === "active" ? "success" : "secondary"} className={className}>
            {status === "active" ? "активний" : status === "blocked" ? "заблок." : status}
        </Badge>
    );
}

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [workshops, setWorkshops] = useState([]);
    const [show, setShow] = useState(false);
    const [mode, setMode] = useState("create");
    const [keycrmRoles, setKeycrmRoles] = useState([]);
    const [showRoles, setShowRoles] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [view, setView] = useState(readStoredView);

    const changeView = (v) => {
        setView(v);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, v);
        } catch {
            /* приватний режим — просто не запам'ятовуємо */
        }
    };
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const fetchUsers = async () => {
        try {
            const res = await axios.get("/api/users", { withCredentials: true });
            setUsers(res.data);
        } catch (e) {
            console.error("Помилка завантаження користувачів", e);
        }
    };

    /** Цехи з обліком працівників («наш цех») — їхні працівники не мають акаунта, але ведуться тут же. */
    const fetchWorkshops = async () => {
        try {
            const res = await axios.get("/api/workshop-payroll/workshops", { withCredentials: true });
            setWorkshops((Array.isArray(res.data) ? res.data : []).filter((w) => w.trackWorkers));
        } catch (e) {
            setWorkshops([]);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchWorkshops();
        fetchKeycrmRoles();
    }, []);

    const fetchKeycrmRoles = async () => {
        try {
            const res = await axios.get("/api/users/keycrm-roles", { withCredentials: true });
            setKeycrmRoles(Array.isArray(res.data) ? res.data : []);
        } catch {
            setKeycrmRoles([]);
        }
    };

    /** Назва ролі як у KeyCRM (довідник «Ролі CRM»); якщо співробітник без ролі CRM — роль застосунку. */
    const displayRole = (u) => {
        const crmRoleId = u.profile?.keycrmRoleId ?? u.keycrm?.roleId;
        const crmName = keycrmRoles.find((r) => r.roleId === crmRoleId)?.name;
        if (!crmName) return roleLabel(u.role, u.profile?.showroomCity);
        const city = u.role === "showroom_manager" ? roleLabel(u.role, u.profile?.showroomCity).match(/\(([^)]+)\)$/)?.[1] : null;
        return city ? `${crmName} (${city})` : crmName;
    };

    /** Заблоковані — завжди в кінці; порядок усередині груп як з сервера (стабільне сортування). */
    const sortedUsers = [...users].sort((a, b) => Number(!!a.blocked) - Number(!!b.blocked));

    const openCreate = () => {
        setMode("create");
        setEditUser(null);
        setShow(true);
    };

    const openEdit = (user) => {
        setMode("edit");
        setEditUser(user);
        setShow(true);
    };


    const handleSyncCrm = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await axios.post("/api/users/sync-keycrm", {}, { withCredentials: true });
            setSyncResult({ ok: true, ...res.data });
            fetchKeycrmRoles();
            clearFopLabelsCache();
            fetchUsers();
        } catch (e) {
            setSyncResult({ ok: false, error: e.response?.data?.error || e.message });
        } finally {
            setSyncing(false);
        }
    };

    const handleDelete = async (id) => {
        if (
            !window.confirm(
                "Видалити користувача назавжди? Історію (графік, транзакції тощо) це не видалить, але посилання стануть «завислими». Для звільнення співробітника краще скористайтесь «Заблокувати»."
            )
        )
            return;

        try {
            await axios.delete(`/api/users/${id}`, { withCredentials: true });
            fetchUsers();
        } catch (e) {
            alert("Помилка видалення");
        }
    };

    const handleToggleBlock = async (u) => {
        const blocked = !u.blocked;
        const msg = blocked
            ? "Заблокувати користувача? Він не зможе увійти в систему, активні сесії буде завершено, а з поточного графіка змін його приберуть (потрапить в «Архів графіку», видимий адміну)."
            : "Розблокувати користувача? Він знову зможе увійти в систему.";
        if (!window.confirm(msg)) return;

        try {
            await axios.patch(
                `/api/users/${u._id}/block`,
                { blocked },
                { withCredentials: true }
            );
            fetchUsers();
        } catch (e) {
            alert(e.response?.data?.error || "Помилка");
        }
    };

    const renderActions = (u) => (
        <>
            <Button size="sm" variant="outline-dark" className="me-2" title="Редагувати" onClick={() => openEdit(u)}>
                <Pencil size={16} aria-hidden />
            </Button>
            <Button
                size="sm"
                variant={u.blocked ? "outline-success" : "outline-warning"}
                className="me-2"
                title={u.blocked ? "Розблокувати" : "Заблокувати"}
                onClick={() => handleToggleBlock(u)}
            >
                {u.blocked ? <Unlock size={16} aria-hidden /> : <Lock size={16} aria-hidden />}
            </Button>
            <Button size="sm" variant="outline-danger" title="Видалити назавжди" onClick={() => handleDelete(u._id)}>
                <Trash2 size={16} aria-hidden />
            </Button>
        </>
    );

    return (
        <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">Користувачі</h4>
                </div>
                <div className="d-flex gap-2">
                    <ButtonGroup aria-label="Вигляд">
                        <Button
                            variant={view === "cards" ? "dark" : "outline-dark"}
                            title="Картки"
                            onClick={() => changeView("cards")}
                        >
                            <LayoutGrid size={16} aria-hidden />
                        </Button>
                        <Button
                            variant={view === "table" ? "dark" : "outline-dark"}
                            title="Таблиця"
                            onClick={() => changeView("table")}
                        >
                            <List size={16} aria-hidden />
                        </Button>
                    </ButtonGroup>
                    <Button variant="outline-dark" onClick={() => setShowRoles(true)} title="Назви та права ролей KeyCRM">
                        Ролі CRM
                    </Button>
                    <Button variant="outline-dark" onClick={handleSyncCrm} disabled={syncing}>
                        {syncing ? (
                            <Spinner size="sm" animation="border" className="me-2" />
                        ) : (
                            <RefreshCw size={16} className="me-2" aria-hidden />
                        )}
                        Оновити з CRM
                    </Button>
                    <Button variant="dark" onClick={openCreate}>
                        + Додати
                    </Button>
                </div>
            </div>

            {syncResult ? (
                <Alert
                    variant={syncResult.ok ? "success" : "danger"}
                    dismissible
                    onClose={() => setSyncResult(null)}
                >
                    {syncResult.ok ? (
                        <>
                            <div className="fw-semibold mb-1">
                                KeyCRM: {syncResult.total} користувачів, оновлено {syncResult.updated}
                            </div>
                            {syncResult.created.length ? (
                                <div>
                                    Створено акаунти ({syncResult.created.length}):{" "}
                                    {syncResult.created.join(", ")} — задайте їм пароль (такий самий, як у CRM).
                                </div>
                            ) : null}
                            {syncResult.blocked.length ? (
                                <div>Заблоковано (як у CRM): {syncResult.blocked.join(", ")}</div>
                            ) : null}
                            {syncResult.linked.length ? (
                                <div>Прив&apos;язано за email: {syncResult.linked.join(", ")}</div>
                            ) : null}
                            {!syncResult.created.length && !syncResult.blocked.length && !syncResult.linked.length ? (
                                <div>Нових співробітників і блокувань немає.</div>
                            ) : null}
                        </>
                    ) : (
                        <>Помилка синхронізації: {syncResult.error}</>
                    )}
                </Alert>
            ) : null}

            {view === "cards" ? (
                <Row xs={1} md={2} xl={3} xxl={4} className="g-3">
                    {sortedUsers.map((u) => {
                        const rawPhone = u.profile?.phone || u.keycrm?.phone;
                        return (
                            <Col key={u._id}>
                                <div className={`users-card${u.blocked ? " users-card--blocked" : ""}`}>
                                    <div className="users-card__body">
                                        <div className="users-card__head">
                                            <div className="users-card__avatar">
                                                <UserAvatar key={u.avatarUrl || "none"} user={{ ...u, blocked: false }} size={56} />
                                                <span
                                                    className={`users-card__status-dot${u.blocked ? " users-card__status-dot--off" : ""}`}
                                                    title={u.blocked ? "Заблокований" : "Активний"}
                                                />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="users-card__name">
                                                    {u.directory?.displayTitle || u.username}
                                                </div>
                                                <div className="users-card__role">
                                                    {displayRole(u)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="users-card__cols">
                                            <div className="users-card__col">
                                                <div className="users-card__meta">
                                                    <div className="users-card__meta-row">
                                                        <Mail size={14} aria-hidden />
                                                        <span>{u.username}</span>
                                                    </div>
                                                    {formatPhone(rawPhone) ? (
                                                        <div className="users-card__meta-row">
                                                            <Phone size={14} aria-hidden />
                                                            <PhoneLink raw={rawPhone} />
                                                        </div>
                                                    ) : null}
                                                    {u.profile?.keycrmManagerId != null ? (
                                                        <div className="users-card__meta-row">
                                                            <Link2 size={14} aria-hidden />
                                                            <span>
                                                                KeyCRM #{u.profile.keycrmManagerId}
                                                                {u.keycrm?.status === "blocked" ? " · заблокований у CRM" : ""}
                                                                {u.keycrm?.lastLoggedAt
                                                                    ? ` · вхід ${formatDateTime(u.keycrm.lastLoggedAt)}`
                                                                    : ""}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </div>
        
                                                {u.needsPassword || u.schedule || u.integrations?.fopSlug ? (
                                                    <div className="users-card__chips">
                                                        {u.needsPassword ? (
                                                            <span className="users-card__chip users-card__chip--warn">
                                                                <KeyRound size={12} aria-hidden />
                                                                Задати пароль
                                                            </span>
                                                        ) : null}
                                                        {u.schedule ? (
                                                            <span className="users-card__chip" title="Група в графіку змін">
                                                                <CalendarDays size={12} aria-hidden />
                                                                {u.schedule.group || "Графік"}
                                                            </span>
                                                        ) : null}
                                                        {u.integrations?.fopSlug ? (
                                                            <span className="users-card__chip" title="Ключ Mono (fopSlug)">
                                                                <Wallet size={12} aria-hidden />
                                                                {u.integrations.fopSlug}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <RequisitesPanel user={u} />
                                        </div>
                                    </div>

                                    <div className="users-card__footer">
                                        <span className="users-card__state">
                                            {u.blocked ? "Заблокований" : "Активний"}
                                        </span>
                                        <div className="users-card__actions">
                                            <button
                                                type="button"
                                                className="users-card__action"
                                                title="Редагувати"
                                                onClick={() => openEdit(u)}
                                            >
                                                <Pencil size={16} aria-hidden />
                                            </button>
                                            <button
                                                type="button"
                                                className={`users-card__action ${u.blocked ? "users-card__action--ok" : "users-card__action--warn"}`}
                                                title={u.blocked ? "Розблокувати" : "Заблокувати"}
                                                onClick={() => handleToggleBlock(u)}
                                            >
                                                {u.blocked ? <Unlock size={16} aria-hidden /> : <Lock size={16} aria-hidden />}
                                            </button>
                                            <button
                                                type="button"
                                                className="users-card__action users-card__action--danger"
                                                title="Видалити назавжди"
                                                onClick={() => handleDelete(u._id)}
                                            >
                                                <Trash2 size={16} aria-hidden />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            ) : (
            <Table bordered hover responsive>
                <thead>
                    <tr>
                        <th>Співробітник</th>
                        <th>Телефон</th>
                        <th>KeyCRM</th>
                        <th>fopSlug</th>
                        <th>Графік змін</th>
                        <th>Mono токен</th>
                        <th>Username</th>
                        <th>Роль</th>
                        <th>Статус</th>
                        <th style={{ width: "150px" }}>Дії</th>
                    </tr>
                </thead>

                <tbody>
                    {sortedUsers.map((u) => (
                        <tr key={u._id} className={u.blocked ? "text-muted" : ""}>
                            <td>
                                <div className="d-flex align-items-center gap-2">
                                    <UserAvatar key={u.avatarUrl || "none"} user={u} />
                                    <div style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-nowrap">
                                            {u.directory?.displayTitle || u.username}
                                        </div>
                                        <div className="small text-secondary text-truncate">
                                            {u.keycrm?.email || u.username}
                                        </div>
                                        {u.needsPassword ? (
                                            <Badge bg="warning" text="dark" className="mt-1">
                                                <KeyRound size={12} className="me-1" aria-hidden />
                                                Задати пароль
                                            </Badge>
                                        ) : null}
                                    </div>
                                </div>
                            </td>
                            <td className="text-nowrap">
                                {formatPhone(u.profile?.phone || u.keycrm?.phone) ? (
                                    <PhoneLink raw={u.profile?.phone || u.keycrm?.phone} />
                                ) : (
                                    "—"
                                )}
                            </td>
                            <td className="small">
                                {u.profile?.keycrmManagerId != null ? (
                                    <>
                                        <div>
                                            #{u.profile.keycrmManagerId}
                                            <CrmStatusBadge status={u.keycrm?.status} />
                                        </div>
                                        {u.keycrm?.lastLoggedAt ? (
                                            <div className="text-secondary" title="Останній вхід у KeyCRM">
                                                вхід {formatDateTime(u.keycrm.lastLoggedAt)}
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    "—"
                                )}
                            </td>
                            <td>
                                <code>{u.integrations?.fopSlug || "—"}</code>
                            </td>
                            <td>
                                {u.schedule ? (
                                    u.schedule.group || <span className="text-muted">без групи</span>
                                ) : (
                                    "—"
                                )}
                            </td>
                            <td>{u.integrationsMeta?.hasMonobankToken ? "так" : "ні"}</td>
                            <td>{u.username}</td>
                            <td>{displayRole(u)}</td>
                            <td>
                                {u.blocked ? (
                                    <Badge bg="secondary">Заблокований</Badge>
                                ) : (
                                    <Badge bg="success">Активний</Badge>
                                )}
                            </td>
                            <td className="text-nowrap">
                                {renderActions(u)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            )}

            {workshops.map((w) => (
                <div key={w._id} className="workshop-payroll-module mt-4">
                    <WorkshopWorkersCrud
                        workshopId={w._id}
                        workers={w.workers}
                        onChanged={fetchWorkshops}
                        title={`Працівники цеху «${w.name}»`}
                    />
                </div>
            ))}

            <UserFormModal
                show={show}
                mode={mode}
                user={editUser}
                keycrmRoles={keycrmRoles}
                onHide={() => setShow(false)}
                onUserPatched={(updated) => {
                    setEditUser((prev) => ({ ...prev, ...updated }));
                    setUsers((list) => list.map((x) => (x._id === updated._id ? { ...x, ...updated } : x)));
                }}
                onSaved={(warning) => {
                    setShow(false);
                    clearFopLabelsCache();
                    fetchUsers();
                    if (warning) alert(warning);
                }}
            />

            <KeycrmRolesModal
                show={showRoles}
                roles={keycrmRoles}
                onHide={() => setShowRoles(false)}
                onSaved={(list) => {
                    setKeycrmRoles(list);
                    setShowRoles(false);
                    fetchUsers();
                }}
            />
        </div>
    );
}
